import { Check, CircleHelp } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";


import Button from "../../lib/ui/components/button/Button";
import Tooltip from "../../lib/ui/components/tooltip/Tooltip";
import Textarea from "../../lib/ui/components/input/Textarea";
import { LabelText, XsmallText } from "../../lib/ui/components/typography/Typography";
import { GENERATE_ANALYSIS_CRITERIA, UPDATE_HIRING_PREFERENCES } from "../../lib/urls";
import { analysisCriteriaToDraft } from "./analysisRubricDraft";
import JobDocumentsUpload from "./JobDocumentsUpload";
import SectionFooter from "../../lib/ui/components/section-footer/SectionFooter";

const snapshotFrom = (idealCandidate, dealbreakers, internalNotes) => ({
    idealCandidate: idealCandidate ?? "",
    dealbreakers: dealbreakers ?? "",
    internalNotes: internalNotes ?? "",
});

const InternalRoleContext = ({
    idealCandidate,
    setIdealCandidate,
    dealbreakers,
    setDealbreakers,
    internalNotes,
    setInternalNotes,
    internalDocuments,
    setInternalDocuments,
    companyId,
    jobId,
    token,
    roleContextCharacterCount,
    roleContextCharacterLimit,
    preferencesRevision = 0,
    canGenerateRules = true,
    hasRules = false,
    onRulesGenerated,
    onGeneratingRulesChange,
    onContinue,
    onBack,
    footerHost,
}) => {
    const [baseline, setBaseline] = useState(() =>
        snapshotFrom(idealCandidate, dealbreakers, internalNotes)
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isGeneratingRules, setIsGeneratingRules] = useState(false);
    const [busyAction, setBusyAction] = useState(null);
    const preferencesRef = useRef({ idealCandidate, dealbreakers, internalNotes });
    preferencesRef.current = { idealCandidate, dealbreakers, internalNotes };

    useEffect(() => {
        const { idealCandidate: ideal, dealbreakers: deals, internalNotes: notes } =
            preferencesRef.current;
        setBaseline(snapshotFrom(ideal, deals, notes));
    }, [preferencesRevision]);

    const isDirty =
        idealCandidate !== baseline.idealCandidate ||
        dealbreakers !== baseline.dealbreakers ||
        internalNotes !== baseline.internalNotes;

    const revertPreferences = () => {
        setIdealCandidate(baseline.idealCandidate);
        setDealbreakers(baseline.dealbreakers);
        setInternalNotes(baseline.internalNotes);
    };

    const persistPreferences = async () => {
        const response = await fetch(UPDATE_HIRING_PREFERENCES(companyId, jobId), {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                ideal_candidate: idealCandidate,
                dealbreakers,
                internal_notes: internalNotes,
            }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.detail || "Could not save hiring preferences");
        }
        setBaseline(snapshotFrom(idealCandidate, dealbreakers, internalNotes));
        return data;
    };

    const savePreferences = async () => {
        setBusyAction("save");
        setIsSaving(true);
        try {
            await persistPreferences();
            toast.success("Hiring preferences saved");
        } catch (error) {
            toast.error(error.message || "Could not save hiring preferences");
        } finally {
            setIsSaving(false);
            setBusyAction(null);
        }
    };

    const saveAndGenerateRules = async () => {
        setBusyAction("generate");
        setIsSaving(true);
        onGeneratingRulesChange?.(true);
        try {
            await persistPreferences();
            setIsGeneratingRules(true);
            const response = await fetch(GENERATE_ANALYSIS_CRITERIA(companyId, jobId), {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.detail || "Could not generate evaluation rules");
            }
            onRulesGenerated?.(analysisCriteriaToDraft(data));
            toast.success("Hiring preferences saved and evaluation rules generated");
        } catch (error) {
            toast.error(error.message || "Could not save hiring preferences");
        } finally {
            setIsSaving(false);
            setIsGeneratingRules(false);
            setBusyAction(null);
            onGeneratingRulesChange?.(false);
        }
    };

    const isBusy = isSaving || isGeneratingRules;
    const canContinue = !isDirty && hasRules;

    return (
        <div className="w-full">
            <div className="mb-3">
                <LabelText weight="normal" className="!mb-0">Hiring Preferences</LabelText>
            </div>
            <div className="flex flex-col gap-3" data-cy="internal-role-context">
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <label htmlFor="ideal-candidate" className="block mb-2">
                        <XsmallText emphasis={true} weight="semibold">What does a great hire look like?</XsmallText>
                    </label>
                    <Textarea
                        id="ideal-candidate"
                        value={idealCandidate}
                        onChange={(e) => setIdealCandidate(e.target.value)}
                        maxLength={5000}
                        rows={4}
                        placeholder="Experience, skills and traits that separate a great candidate from an average one"
                    />
                </div>

                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <label htmlFor="dealbreakers" className="block mb-2">
                        <XsmallText emphasis={true} weight="semibold">Dealbreakers</XsmallText>
                    </label>
                    <Textarea
                        id="dealbreakers"
                        value={dealbreakers}
                        onChange={(e) => setDealbreakers(e.target.value)}
                        maxLength={5000}
                        rows={3}
                        placeholder="Anything that rules a candidate out, e.g. can't work required hours"
                    />
                </div>

                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <div className="flex items-center mb-2">
                        <label htmlFor="internal-notes">
                            <XsmallText emphasis={true} weight="semibold">Additional role information</XsmallText>
                        </label>
                        <Tooltip position="right" content="Add any relevant information here that you don't want shared with the candidate">
                            <CircleHelp size="1em" className="text-gray-500 cursor-pointer ml-2" />
                        </Tooltip>
                    </div>
                    <Textarea
                        id="internal-notes"
                        value={internalNotes}
                        onChange={(e) => setInternalNotes(e.target.value)}
                        maxLength={20000}
                        rows={12}
                        placeholder="Paste anything else about the role: hiring manager notes, team context, seniority expectations, past hires, salary band, whatever you have."
                    />
                </div>

                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <div className="mb-2">
                        <XsmallText emphasis={true} weight="semibold">Internal documents</XsmallText>
                        <XsmallText>
                            Scorecards, hiring manager notes, interview guides — used for scoring, never shown to candidates.
                        </XsmallText>
                    </div>
                    <JobDocumentsUpload
                        documents={internalDocuments}
                        onChange={setInternalDocuments}
                        dataCy="internal-documents-input"
                        audience="internal"
                        companyId={companyId}
                        jobId={jobId}
                        token={token}
                        contextCount={roleContextCharacterCount}
                        contextLimit={roleContextCharacterLimit}
                    />
                </div>
            </div>
            <SectionFooter
                host={footerHost}
                leading={
                    <Button variant="ghost" onClick={onBack} disabled={isBusy} data-cy="hiring-preferences-back-button">
                        Back
                    </Button>
                }
            >
                <Button
                    variant="secondary"
                    onClick={revertPreferences}
                    disabled={!isDirty || isBusy}
                    data-cy="reset-hiring-preferences-button"
                >
                    Reset
                </Button>
                <Button
                    variant="secondary"
                    onClick={savePreferences}
                    disabled={!isDirty || isBusy}
                    data-cy="save-hiring-preferences-button"
                >
                    {busyAction === "save" ? "Saving..." : "Save"}
                </Button>
                <Button
                    variant="primary"
                    onClick={canContinue ? onContinue : saveAndGenerateRules}
                    disabled={isBusy || (canContinue ? false : (!isDirty || !canGenerateRules))}
                    leftIcon={<Check size="1em" />}
                    data-cy="save-and-generate-rules-button"
                >
                    {isGeneratingRules ? "Generating..." : busyAction === "generate" ? "Saving..." : canContinue ? "Continue" : "Save & Generate Rules"}
                </Button>
            </SectionFooter>
        </div>
    );
};

export default React.memo(InternalRoleContext);
